import { Alert, CircularProgress, debounce, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import moment from 'moment';
import { FormEventHandler, useEffect, useMemo, useState } from 'react';
import { InlineLoader } from '../../common/components/InlineLoader';
import { getErrorMessage } from '../../common/errors/errors';
import { useFormItem } from '../../common/hooks/use-form-item';
import { InputType } from '../../common/hooks/use-form-item/types';
import { and } from '../../common/hooks/use-form-item/validators/compositions';
import { required } from '../../common/hooks/use-form-item/validators/existential';
import { greaterOrEqualThan, lessOrEqualThan, maxLength } from '../../common/hooks/use-form-item/validators/operational';
import { Profile } from '../../common/models/Profile';
import { getScanPlatformLabel, ScanPlatform } from '../../organization/scans/enums/ScanPlatform';
import { ScansService } from '../../organization/scans/services/ScansService';
import { Project } from '../../projects/common/models/Project';
import { OrganizationService } from '../services/OrganizationService';
import { ButtonBox, PrimaryButton, TertiaryButton } from './Buttons';
import { DateUtils } from '../../common/utils/DateUtils';

type Props = {
  submit: {
    label: string;
    action: (
      name: string,
      projectId: number,
      profileId: number | null,
      platform: ScanPlatform,
      hostname: string | null,
      lastSeenHours: number | null,
      workLoad: number | null,
      maxFilesPerSecond: number | null,
      maxFileSize: number | null,
      exclude: string[],
      tags: string[]
    ) => Promise<void>;
  };
  back: {
    label: string;
    action: () => void;
  };
};

const HOST_ACTIVITY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24];

const ProjectOption = (props: { project: Project }) => {
  const overflowSx = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

  return (
    <Stack rowGap={0.5} overflow="hidden">
      <Typography variant="body1" sx={overflowSx}>
        {props.project.getName()}
      </Typography>
    </Stack>
  );
};

const ProfileOption = (props: { profile: Profile }) => {
  const overflowSx = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

  return (
    <Stack rowGap={0.5} overflow="hidden">
      <Typography variant="body1" sx={overflowSx}>
        {props.profile.getName()}
      </Typography>
      <Typography variant="overline" sx={{ ...overflowSx, color: 'grey.600', lineHeight: 1, pb: 0.5 }}>
        {props.profile.getTypeLabel()}
      </Typography>
    </Stack>
  );
};
export const CreateScanForm = ({ submit, back }: Props) => {
  const [scanService] = useState<ScansService>(new ScansService());
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingEstimatedHosts, setLoadingEstimatedHosts] = useState<boolean>(false);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [profiles, setProfiles] = useState<Profile[] | null>(null);
  const [affectedHosts, setAffectedHosts] = useState<number | null>(null);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const organizationService = new OrganizationService();

    const fetchProjects = async () => {
      try {
        const projects = await organizationService.getProjects();
        setProjects(projects);
      } catch (e: unknown) {
        setError(getErrorMessage(e));
      }
    };

    const fetchProfiles = async () => {
      try {
        const profiles = await organizationService.getProfiles();
        setProfiles(profiles);
      } catch (e: unknown) {
        setError(getErrorMessage(e));
      }
    };

    fetchProjects();
    fetchProfiles();

    // Cancel all requests on unmount
    return () => organizationService.cancelAllRequests();
  }, []);

  const defaultScanName = useMemo(() => {
    return `Scan [${DateUtils.toDateTimeString(moment())}]`;
  }, []);

  const [name, nameComponent, isNameValid] = useFormItem<string>({
    type: InputType.TYPE_TEXT,
    label: t('createScanForm.name.input'),
    value: defaultScanName,
    validator: and(required(), maxLength(1000)),
    inputOptions: {
      autoFocus: true,
      disabled: loading,
      focusedLabel: t('createScanForm.name.focused'),
      showRequiredIndicator: true,
    },
    textOptions: { trimOnBlur: true },
  });

  const [projectId, projectComponent, isProjectValid] = useFormItem<number | null>({
    type: InputType.TYPE_AUTOCOMPLETE,
    label: t('createScanForm.project.input'),
    value: null,
    validator: required(),
    inputOptions: {
      disabled: loading,
      focusedLabel: t('createScanForm.project.focused'),
      showRequiredIndicator: true,
    },
    autocompleteOptions: {
      readOnly: false,
      clearable: false,
      options: projects?.map((p) => ({
        value: p.getId(),
        label: p.getName(),
        element: <ProjectOption project={p} />,
        nativeOptionHover: true,
      })),
    },
  });

  const [profileId, profileComponent, isProfileValid] = useFormItem<number | null>({
    type: InputType.TYPE_AUTOCOMPLETE,
    label: t('createScanForm.profile.input'),
    value: null,
    validator: null,
    inputOptions: {
      disabled: loading,
      focusedLabel: t('createScanForm.profile.focused'),
    },
    autocompleteOptions: {
      readOnly: false,
      clearable: true,
      options: profiles?.map((p) => ({
        value: p.getId(),
        label: p.getName(),
        element: <ProfileOption profile={p} />,
        nativeOptionHover: true,
      })),
    },
  });

  const [workLoad, workLoadComponent] = useFormItem<number | null>({
    type: InputType.TYPE_INTEGER,
    label: t('createScanForm.workLoad.input'),
    value: null,
    validator: and(greaterOrEqualThan(0), lessOrEqualThan(100)),
    inputOptions: {
      disabled: loading,
      focusedLabel: t('createScanForm.workLoad.focused'),
    },
  });

  const [maxFilesPerSecond, maxFilesPerSecondComponent] = useFormItem<number | null>({
    type: InputType.TYPE_INTEGER,
    label: t('createScanForm.maxFilesPerSecond.input'),
    value: null,
    validator: and(greaterOrEqualThan(0), lessOrEqualThan(10000)),
    inputOptions: {
      disabled: loading,
      focusedLabel: t('createScanForm.maxFilesPerSecond.focused'),
    },
  });

  const [maxFileSize, maxFileSizeComponent] = useFormItem<number | null>({
    type: InputType.TYPE_INTEGER,
    label: t('createScanForm.maxFileSize.input'),
    value: null,
    validator: and(greaterOrEqualThan(0), lessOrEqualThan(1000000000)),
    inputOptions: {
      disabled: loading,
      focusedLabel: t('createScanForm.maxFileSize.focused'),
    },
  });

  const [exclude, excludeComponent] = useFormItem<string[]>({
    type: InputType.TYPE_FREE_TAGS,
    label: t('createScanForm.exclude.input'),
    value: [],
    validator: null,
    inputOptions: {
      disabled: loading,
      focusedLabel: t('createScanForm.exclude.focused'),
    },
  });

  const [tags, tagsComponent] = useFormItem<string[]>({
    type: InputType.TYPE_FREE_TAGS,
    label: t('createScanForm.tags.input'),
    value: [],
    validator: null,
    inputOptions: {
      disabled: loading,
      focusedLabel: t('createScanForm.tags.focused'),
    },
  });

  const [platform, platformComponent, isPlatformValid] = useFormItem<ScanPlatform>({
    type: InputType.TYPE_AUTOCOMPLETE,
    label: t('createScanForm.platform.input'),
    value: ScanPlatform.LINUX,
    validator: required(),
    inputOptions: {
      disabled: loading,
      focusedLabel: t('createScanForm.platform.focused'),
      showRequiredIndicator: true,
    },
    autocompleteOptions: {
      readOnly: false,
      clearable: false,
      options: [ScanPlatform.LINUX, ScanPlatform.WINDOWS].map((p) => ({
        value: p,
        label: getScanPlatformLabel(p),
        nativeOptionHover: true,
      })),
    },
  });

  const [hostName, hostNameComponent, isHostNameValid] = useFormItem<string | null>({
    type: InputType.TYPE_TEXT,
    label: t('createScanForm.hostname.input'),
    value: '',
    validator: maxLength(1000),
    inputOptions: {
      disabled: loading,
      focusedLabel: t('createScanForm.hostname.focused'),
      help: t('createScanForm.hostname.help'),
    },
    textOptions: { trimOnBlur: true },
  });

  const [hostActivity, hostActivityComponent, isHostActivityValid] = useFormItem<number | null>({
    type: InputType.TYPE_AUTOCOMPLETE,
    label: t('createScanForm.hostActivity.input'),
    value: null,
    validator: null,
    inputOptions: {
      disabled: loading,
      focusedLabel: t('createScanForm.hostActivity.focused'),
      help: t('createScanForm.hostActivity.help'),
    },
    autocompleteOptions: {
      readOnly: true,
      clearable: true,
      options: HOST_ACTIVITY_OPTIONS.map((a) => ({ value: a, label: t('hostActivity', { value: a }) })),
    },
  });

  const fetchAffectedHosts = useMemo(() => {
    const fetchAffectedHosts = async (platform: ScanPlatform, hostName: string | null, lastSeenHours: number | null) => {
      try {
        setLoadingEstimatedHosts(true);

        const hosts = await scanService.estimateCrowdStrikeHosts({
          platform,
          hostname: hostName,
          lastSeenHours: lastSeenHours,
        });
        setAffectedHosts(hosts);
        setLoadingEstimatedHosts(false);
      } catch {
        setAffectedHosts(null);
        setLoadingEstimatedHosts(false);
      }
    };

    return debounce(fetchAffectedHosts, 200);
  }, [scanService]);

  useEffect(() => {
    setLoadingEstimatedHosts(true);
    fetchAffectedHosts(platform, hostName, hostActivity);

    return () => scanService.cancelAllRequests();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, hostName, hostActivity]);

  const valuesValid = !!name && !!projectId && !!platform;
  const formValid = isNameValid && isProjectValid && isProfileValid && isPlatformValid && isHostNameValid && isHostActivityValid;
  const canSubmit = !loading && valuesValid && formValid;

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setLoading(true);
      setError(null);
      await submit.action(name, projectId, profileId, platform, hostName, hostActivity, workLoad, maxFilesPerSecond, maxFileSize, exclude, tags);
    } catch (e: unknown) {
      setLoading(false);
      setError(getErrorMessage(e));
    }
  };

  const dataLoaded = !!projects && !!profiles;

  return (
    <Stack spacing={1.5} component="form" onSubmit={onSubmit}>
      {error && <Alert severity="error">{error}</Alert>}
      {!error && !dataLoaded && <InlineLoader />}
      {dataLoaded && (
        <>
          {nameComponent}
          <Stack direction={'row'} gap={1}>
            {projectComponent}
            {profileComponent}
          </Stack>
          {tagsComponent}
          <Stack direction={'row'} gap={1}>
            {workLoadComponent}
            {maxFilesPerSecondComponent}
            {maxFileSizeComponent}
          </Stack>
          <Stack direction={'row'} gap={1}>
            {platformComponent}
            {excludeComponent}
          </Stack>
          <Stack direction={'row'} gap={1}>
            {hostNameComponent}
            {hostActivityComponent}
          </Stack>
        </>
      )}
      <Stack sx={{ mt: 1 }}>
        <Typography variant="caption">{t('createScanForm.affectedHosts')}</Typography>
        <Typography
          variant="h4"
          component="h3"
          sx={{
            opacity: loadingEstimatedHosts ? 0.5 : 1,
            transition: 'all 0.3s ease-in-out',
          }}
        >
          {affectedHosts === null ? t('createScanForm.estimatingAffectedHosts') : affectedHosts}{' '}
          {loadingEstimatedHosts && <CircularProgress color="inherit" size="2rem" aria-label={t('a11y.general.loading')} />}
        </Typography>
      </Stack>

      {dataLoaded && (
        <ButtonBox>
          <TertiaryButton disabled={loading} onClick={back.action}>
            {back.label}
          </TertiaryButton>
          <PrimaryButton disabled={!canSubmit} type="submit">
            {submit.label}
          </PrimaryButton>
        </ButtonBox>
      )}
    </Stack>
  );
};
